import { useAuth } from '@shared/common/providers/AuthProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';
import { nextProjectUrl } from '@shared/constants/urls';
import { truncateDecimals } from '@shared/helpers/formatters';

import { useSqrTokenPriceQuery } from '@query/token';

import { MIXPANEL_EVENT_NAMES } from '@api/mixPanel';
import type { ApplicationHotOfferSchemaPayload } from '@api/types/applications';

import { HotOfferCard } from '@ui/uikit/components/common/cards/HotOfferCard';
import { currencyMap } from '@ui/uikit/components/common/cards/HotOfferCard/internals/TokenRewardChip';

import { HotOfferSharingControl, HotOfferSharingController } from './HotOfferSharingControl';

type Props = {
  offer: ApplicationHotOfferSchemaPayload;
};

export const HotOfferCardWithAmountCalculations = ({ offer }: Props) => {
  const { activePayoutToken } = offer;
  const showSqrTokenPrice = activePayoutToken?.symbol === 'USDT-SQR-EXCH';

  const {
    data: sqrTokenPrice,
  } = useSqrTokenPriceQuery({
    enabled: showSqrTokenPrice,
  });

  const { user } = useAuth();
  const { track } = useMixPanel();

  const handleTrackedLinkClick = (name: string) => {
    track(MIXPANEL_EVENT_NAMES.HOT_OFFER_VIEW_BUTTON_CLICK, {
      distinct_id: user?.id,
      offer: name,
    });
  };

  const amount = showSqrTokenPrice
    ? +truncateDecimals(
        Number(activePayoutToken?.amount) / (sqrTokenPrice?.attributes?.price ?? 1),
        2,
      )
    : activePayoutToken?.amount ?? 0;

  const currencyConfig = currencyMap[activePayoutToken?.symbol] || currencyMap.DEFAULT;

  return (
    <HotOfferCard
      offer={{
        ...offer,
        activePayoutToken: { ...offer.activePayoutToken, amount: amount },
      }}
      link={nextProjectUrl.store(`/hot-offers/${offer.offerId}`)}
      onViewOfferClick={handleTrackedLinkClick}
      sharingControl={
        <HotOfferSharingController
          offer={{
            ...offer,
            activePayoutToken: {
              ...offer.activePayoutToken,
              amount: amount,
              symbol: `${currencyConfig.displayName || offer.activePayoutToken?.symbol || ''}`,
            },
          }}
        >
          <HotOfferSharingControl />
        </HotOfferSharingController>
      }
    />
  );
};
